import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { dataDate, dayRange } from '@/features/dates/date-helpers';
import type { DetailedCalculationResult } from '../../calculations/types';
import { formatDate, getPreviousDayUTC } from '../../date-utils';
import { useNetBenefitData } from '../../hooks/use-net-benefit-data';
import { DebugInfo } from '../debug-info';

export function DetailedNetBenefitTable() {
	const {
		processedData: data,
		rawCalculatedData: rawData,
		loading,
		error,
		checkIn,
		checkOut,
	} = useNetBenefitData();

	const displayDates = dayRange(checkIn, getPreviousDayUTC(checkOut)).map(
		(date) => {
			return {
				displayDate: formatDate(date),
				dataDate: date,
			};
		},
	);

	// Function to enhance the debug info to show the calculation details
	const enhanceDebugInfo = (rawValue: any): any => {
		if (!rawValue) return undefined;

		// If it's a DetailedCalculationResult object
		if (rawValue && typeof rawValue === 'object' && 'details' in rawValue) {
			const detailedResult = rawValue as DetailedCalculationResult<
				any,
				any,
				any
			>;
			return {
				result: detailedResult.value,
				...detailedResult.details,
			};
		}

		return rawValue;
	};

	return (
		<div className='tw-mt-6 tw-mb-8 tw-overflow-x-auto tw-rounded-md tw-border tw-shadow-sm'>
			<div className='tw-bg-secondary tw-p-3 tw-border-b'>
				<h3 className='tw-text-lg tw-font-medium'>
					Detailed Net Benefit Estimate
				</h3>
			</div>
			{loading ? (
				<div className='tw-p-4 tw-text-center'>Loading data...</div>
			) : error ? (
				<div className='tw-p-4 tw-text-center'>
					Error loading data: {error.message}
				</div>
			) : !data ? (
				<div className='tw-p-4 tw-text-center'>No data available.</div>
			) : (
				<Table>
					<TableHeader>
						<TableRow className='tw-bg-white hover:tw-bg-secondary'>
							<TableHead className='tw-w-[300px] tw-font-semibold'>
								Metric
							</TableHead>
							<TableHead className='tw-font-semibold'>Total</TableHead>
							{displayDates.map((date) => (
								<TableHead key={date.dataDate} className='tw-font-semibold'>
									{date.displayDate}
								</TableHead>
							))}
						</TableRow>
					</TableHeader>
					<TableBody>
						{data?.map((row, index) => (
							<TableRow
								key={index}
								className={
									row.isHighlighted
										? 'tw-font-bold tw-bg-white hover:tw-bg-secondary'
										: 'tw-bg-white hover:tw-bg-secondary'
								}
							>
								<TableCell className='tw-font-medium'>{row.label}</TableCell>
								<TableCell
									className={row.isNegative ? 'tw-text-destructive' : ''}
								>
									{row.total}
								</TableCell>
								{displayDates.map((date) => {
									const raw = rawData?.find(
										(r) => dataDate(r.stay_date) === dataDate(date.dataDate),
									);

									// Get the raw value for potentially enhanced debug info
									const rawValue =
										raw && row.key
											? raw[row.key as keyof typeof raw]
											: undefined;

									// Enhance the debug info with calculation details
									const enhancedDebugInfo = enhanceDebugInfo(rawValue);

									return (
										<TableCell
											key={date.dataDate}
											className={
												row.dateValues[date.dataDate]?.isNegative
													? 'tw-text-destructive'
													: ''
											}
										>
											<DebugInfo
												data={enhancedDebugInfo}
												label={row.dateValues[date.dataDate]?.value || '-'}
											/>
										</TableCell>
									);
								})}
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</div>
	);
}
